import React, { useContext } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import { AppContext } from "../../context/app.context";
import { ScheduleContext } from "../../context/schedule.context";
import { ModelTsContext } from "../../context/modelTS.context";
import {
  getDropDateForRepairOrder,
  computeEstimatedCompletion,
} from "./RepairOrderConfirmModal";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #166 — WO Workorder pre-assigned-installer confirmation modal.
//
// Sibling of `RepairOrderConfirmModal` (#154). Same UI shape, same drop-
// type categorization, same six choice scenarios. The DIFFERENCES are:
//
//   1. Discriminator: gated by `lowes_projectNo` starting with "WO"
//      (case-insensitive) — see `isWoWorkorderWithInstaller.js`. PROD
//      data investigation (dbtools/src/queries/wo-workorder-discriminator-
//      investigate.mjs) confirmed this is the right discriminator;
//      `classification === "Workorder/Expense"` only intersected 5 of
//      1288 affected records and incorrectly caught 1504 expense records.
//   2. Modal copy: "Workorder" instead of "Repair Order". Header shows
//      the WO project# (e.g. WO003238950) as the operational identifier
//      since that's how the field uses these records.
//   3. rts-keep / rts-clear statusCode: rolls back to 1, 2, or 3 based
//      on the WO record's underlying FM flags (notVerifiedByStaff →
//      status 1; notMaterialReceived → status 2; otherwise status 3) —
//      mirrors the existing RTS-revert logic in
//      schedule.context.js. Repair Orders use statusCode 30 here because
//      they have their own "Open Repair Order" parking state; WO
//      Workorders don't, so they use the normal RTS rollback semantics.
//
// All other six-choice flow (cell-keep, cell-reassign, unassigned-keep,
// unassigned-clear, rts-keep, rts-clear) and chain-to-JobInitModal
// behavior are identical to #154. See RepairOrderConfirmModal for the
// architectural rationale.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Pure: builds the `pendingDragUpdate` payload for each of the six
 * possible user choices. Extracted so tests verify the payload byte-for-
 * byte without spinning up the React tree.
 *
 * Mirrors `buildRepairOrderPendingUpdate` exactly EXCEPT for the
 * statusCode used in `rts-keep` / `rts-clear` paths — see header doc.
 *
 * @param {Object} params
 * @param {string} params.choice - One of:
 *   "cell-keep"          → cell drop, keep original installer
 *   "cell-reassign"      → cell drop, reassign to drop target
 *   "unassigned-keep"    → unassigned-with-date drop, keep + schedule on
 *                          original installer
 *   "unassigned-clear"   → unassigned-with-date drop, clear installer +
 *                          keep dropped date
 *   "rts-keep"           → RTS drop, keep payroll# + rollback status
 *   "rts-clear"          → RTS drop, clear payroll# + rollback status
 * @param {Object} params.foundDocument - The WO Workorder being dragged.
 *   Read-only (must not be mutated per #85). Used to read
 *   `notVerifiedByStaff` / `notMaterialReceived` for the RTS rollback
 *   status.
 * @param {string} params.dropTargetInstaller - Payroll# of the cell the
 *   user dropped on (only used by "cell-reassign"). "" for unassigned/RTS.
 * @param {string} params.dropDate - Drop-target date string ("" for RTS).
 * @returns {Object} pendingDragUpdate payload.
 */
export function buildWoWorkorderPendingUpdate({
  choice,
  foundDocument,
  dropTargetInstaller,
  dropDate,
}) {
  const originalInstaller = foundDocument?.installer ?? "";
  const estComp = computeEstimatedCompletion({ foundDocument, dropDate });
  // RTS rollback status — same logic as the existing RTS-clear path in
  // schedule.context.js processDragNDrop (line ~3846). Applied to BOTH
  // rts-keep and rts-clear so the only difference between Keep and Clear
  // for an RTS drop is the `installer` field.
  const rtsRollbackStatus = foundDocument?.notVerifiedByStaff
    ? 1
    : foundDocument?.notMaterialReceived
    ? 2
    : 3;

  switch (choice) {
    case "cell-keep":
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: originalInstaller,
        statusCode: 4,
      };
    case "cell-reassign":
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: dropTargetInstaller,
        statusCode: 4,
      };
    case "unassigned-keep":
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: originalInstaller,
        statusCode: 4,
        extraCellId: null,
        extraCellText: null,
      };
    case "unassigned-clear":
      // Stay SCHEDULED on the dropped date but with empty installer.
      // Mirrors the #154 unassigned-clear behavior — preserves the date
      // the user chose; only the installer is cleared.
      return {
        date_scheduled: dropDate,
        date_estimatedCompletion: estComp,
        installer: "",
        statusCode: 4,
        extraCellId: null,
        extraCellText: null,
      };
    case "rts-keep":
      return {
        date_scheduled: "",
        date_estimatedCompletion: "",
        installer: originalInstaller,
        statusCode: rtsRollbackStatus,
        extraCellId: null,
        extraCellText: null,
      };
    case "rts-clear":
      return {
        date_scheduled: "",
        date_estimatedCompletion: "",
        installer: "",
        statusCode: rtsRollbackStatus,
        extraCellId: null,
        extraCellText: null,
      };
    default:
      // Defensive: never happens in production. Returning {} signals "no
      // change" to buildDragNDropUpdateFields (all fields would compare
      // equal to foundDocument).
      return {};
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function WoWorkorderConfirmModal({ setOpenWoWorkorderConfirmModal }) {
  const {
    openWoWorkorderConfirmModal,
    weekDatesArray,
    dataFormatting_payrollFields,
    setOpenJobInitModal,
  } = useContext(AppContext);
  const { awsgwCandidatesResult } = useContext(ModelTsContext);
  const {
    setDragObject,
    setDropObject,
    setProcessingDragNDrop,
    setPendingDragUpdate,
    dropObject,
  } = useContext(ScheduleContext);

  const foundDocument = openWoWorkorderConfirmModal?.foundDocument || {};
  const dropType = openWoWorkorderConfirmModal?.dropType || "cell";
  const dropTargetInstaller =
    openWoWorkorderConfirmModal?.dropTargetInstaller || "";
  const dropDate = getDropDateForRepairOrder({ dropObject, weekDatesArray });
  const originalInstaller = foundDocument.installer || "";
  const projectNo = foundDocument.lowes_projectNo || "";

  // - - - - - - - - - -

  // Mirrors #154's dispatch flow. Cell + unassigned-with-date drops chain
  // to JobInitModal so the user still picks arrival time + adjusts est
  // complete through the existing reschedule-reason flow. RTS drops
  // bypass JobInitModal (no arrival window for unscheduled state).
  const dispatchChoice = (choice) => {
    if (choice === "rts-keep" || choice === "rts-clear") {
      const payload = buildWoWorkorderPendingUpdate({
        choice,
        foundDocument,
        dropTargetInstaller,
        dropDate,
      });
      setPendingDragUpdate(payload);
      setProcessingDragNDrop(true);
      setOpenWoWorkorderConfirmModal({ show: false });
      return;
    }
    // Cell + Unassigned-with-date: chain to JobInitModal with the
    // user's installer choice pre-selected. JobInitModal's existing
    // `computeJobInitValues` already produces the right defaults for:
    //   - cell-reassign  → installer = dropTargetInstaller (default cell behavior)
    //   - unassigned-clear → installer = ""                (default unassigned behavior)
    // For the two "Keep" choices we pass an override:
    //   - cell-keep        → installer = originalInstaller
    //   - unassigned-keep  → installer = originalInstaller (rendered on
    //                        original installer's row at the dropped date)
    //
    // Reuses the same `repairOrderInstallerOverride` prop on
    // setOpenJobInitModal — the field name is historical (added for #154)
    // but the JobInitModal treats it as a generic "installer override
    // from a pre-assigned-record modal". No semantic distinction needed.
    const repairOrderInstallerOverride =
      choice === "cell-keep" || choice === "unassigned-keep"
        ? originalInstaller
        : null;
    setOpenWoWorkorderConfirmModal({ show: false });
    setOpenJobInitModal({
      show: true,
      repairOrderInstallerOverride,
    });
  };

  const onCancel = () => {
    setDragObject("");
    setDropObject({ id: "" });
    setOpenWoWorkorderConfirmModal({ show: false });
  };

  // - - - - - - - - - -

  // Compliance hold check (mirrors JobInitModal's useEffect; only relevant
  // when the user is REASSIGNING to a new installer on a specific date).
  const isComplianceHoldBlockingReassign = (() => {
    if (dropType !== "cell") return false;
    if (!dropTargetInstaller || !dropDate) return false;
    const installerDoc = awsgwCandidatesResult?.find(
      (doc) =>
        dataFormatting_payrollFields(doc.payrollNo) === dropTargetInstaller
    );
    if (!installerDoc?.Scheduling_Hold_Effective_Date) return false;
    const target = new Date(dropDate);
    const effective = new Date(installerDoc.Scheduling_Hold_Effective_Date);
    target.setHours(0, 0, 0, 0);
    effective.setHours(0, 0, 0, 0);
    return target >= effective;
  })();

  // - - - - - - - - - -

  const renderHeading = () => {
    if (dropType === "cell") {
      return (
        <>
          This Workorder is pre-assigned to installer{" "}
          <span className="font-bold">{originalInstaller}</span>. You dropped
          it on <span className="font-bold">{dropTargetInstaller}</span>
          {dropDate ? (
            <>
              {" "}
              for <span className="font-bold">{dropDate}</span>
            </>
          ) : null}
          . Which installer should keep the assignment?
        </>
      );
    }
    if (dropType === "unassigned-with-date") {
      return (
        <>
          This Workorder is pre-assigned to installer{" "}
          <span className="font-bold">{originalInstaller}</span>. You dropped
          it on <span className="font-bold">Unassigned</span>
          {dropDate ? (
            <>
              {" "}
              for <span className="font-bold">{dropDate}</span>
            </>
          ) : null}
          . Keep the payroll# or clear it?
        </>
      );
    }
    // rts
    return (
      <>
        This Workorder is pre-assigned to installer{" "}
        <span className="font-bold">{originalInstaller}</span>. You dropped it
        on <span className="font-bold">RTS</span> (unscheduled). Keep the
        payroll# on the record or clear it?
      </>
    );
  };

  const renderButtons = () => {
    if (dropType === "cell") {
      return (
        <>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("cell-keep")}
          >
            Keep {originalInstaller}
          </Button>
          <Button
            className="uppercase"
            disabled={isComplianceHoldBlockingReassign}
            title={
              isComplianceHoldBlockingReassign
                ? "Drop target is on compliance hold for this date."
                : undefined
            }
            onClick={() =>
              !isComplianceHoldBlockingReassign &&
              dispatchChoice("cell-reassign")
            }
          >
            Reassign to {dropTargetInstaller}
          </Button>
          <Button color="light" className="uppercase" onClick={onCancel}>
            Cancel
          </Button>
        </>
      );
    }
    if (dropType === "unassigned-with-date") {
      return (
        <>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("unassigned-keep")}
          >
            Keep {originalInstaller} on {dropDate}
          </Button>
          <Button
            className="uppercase"
            onClick={() => dispatchChoice("unassigned-clear")}
          >
            Clear & leave Unassigned
          </Button>
          <Button color="light" className="uppercase" onClick={onCancel}>
            Cancel
          </Button>
        </>
      );
    }
    // rts
    return (
      <>
        <Button className="uppercase" onClick={() => dispatchChoice("rts-keep")}>
          Keep {originalInstaller} (unscheduled)
        </Button>
        <Button className="uppercase" onClick={() => dispatchChoice("rts-clear")}>
          Clear payroll#
        </Button>
        <Button color="light" className="uppercase" onClick={onCancel}>
          Cancel
        </Button>
      </>
    );
  };

  return (
    <Modal onClose={onCancel} disableCloseOnClickOutSide>
      <Modal.Header>
        Workorder{" "}
        <span className="font-bold">{projectNo}</span>
        {foundDocument.invoiceNo ? (
          <span className="opacity-70 text-base">
            {" "}
            (Invoice #{foundDocument.invoiceNo})
          </span>
        ) : null}
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-3 max-w-lg">
          <div>{renderHeading()}</div>
          {foundDocument.customer_lastName ? (
            <div className="text-xs opacity-70">
              Customer: {foundDocument.customer_lastName},{" "}
              {foundDocument.customer_firstName}
            </div>
          ) : null}
          {isComplianceHoldBlockingReassign ? (
            <div className="text-xs font-bold" style={{ color: "#dc2626" }}>
              Reassignment blocked: target installer is on compliance hold for
              this date.
            </div>
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex flex-wrap gap-3 justify-center">
          {renderButtons()}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default WoWorkorderConfirmModal;
